import React from 'react';
import type { PropsWithChildren } from 'react';

import { Box, Col } from '@opendoor/bricks-next';
import type { NavHeaderData } from 'cms/withNavHeader';
import Header from 'components/landing-pages-v2/shared/header/Header';
import { formatStreetAddress, isBuyDirectEligible } from 'components/marketplace/helpers';
import type { PropertyDetailsType } from 'components/marketplace/property';
import { getSeoFields } from 'components/properties/helpers/property';
import SeoHead from 'components/properties/shared/SeoHead';
import GlobalStyles from 'components/shared/GlobalStyles';
import Head from 'next/head';

const mainNavLinks = [
  {
    name: 'How it works',
    href: '/how-it-works-sell',
  },
  {
    name: 'Browse homes',
    href: '/homes',
  },
];

const menuPanelNavLinks = [
  {
    name: 'Work with a builder',
    href: '/builder',
  },
  {
    name: 'Opendoor for Agents',
    href: '/agents',
  },
];

const MarketplaceLayout: React.FC<
  PropsWithChildren<{ property?: PropertyDetailsType; navHeader?: NavHeaderData | null }>
> = ({ children, property, navHeader }) => {
  const resolvedMainNavLinks = navHeader?.mainNavLinks ?? mainNavLinks;
  const resolvedMenuPanelNavLinks = navHeader?.menuPanelNavLinks ?? menuPanelNavLinks;
  const megaNavData = navHeader?.megaNavData;

  const { coverImage, readableAddress, title, canonicalUrl } = getSeoFields(
    property?.addressToken,
    property?.slug,
    undefined, // status is different
    property?.street,
    property?.unitType,
    property?.unitNumber,
    property?.city,
    property?.state,
    property?.zip,
    property?.sqFtLot,
    property?.bedrooms,
    property?.bathrooms,
    property?.yearBuilt,
    property?.photos?.[0],
  );
  const metaDescription = `View photos, details, and schedule a self-tour of this ${property?.bedrooms} bedroom, ${property?.bathrooms} bathroom, ${property?.sqFtTotalLiving} sqft home located at ${readableAddress}.`;
  const seoHead = (
    <SeoHead
      base={{
        title,
        author: 'Opendoor, Inc.',
        metaDescription,
        canonicalUrl,
      }}
      og={{
        url: canonicalUrl,
        title,
        description: metaDescription,
        siteName: 'Opendoor',
        image: coverImage,
        imageAlt: readableAddress
          ? `Photo of home for sale at ${readableAddress}`
          : 'Home for sale on Opendoor',
        type: 'website',
      }}
      schema={{
        name: readableAddress,
        description: metaDescription,
        url: canonicalUrl,
        address: {
          ...(property?.street && {
            streetAddress:
              formatStreetAddress(property.street, property.unitType, property.unitNumber) ||
              undefined,
          }),
          ...(property?.city && { addressLocality: property.city }),
          ...(property?.state && { addressRegion: property.state }),
          ...(property?.zip && { postalCode: property.zip }),
        },
        ...(property?.sqFtLot && {
          floorSize: {
            value: property.sqFtLot.toString(),
          },
        }),
        ...(property?.yearBuilt && {
          yearBuilt: property.yearBuilt.toString(),
        }),
        ...(property?.bedrooms && {
          numberOfBedrooms: property.bedrooms.toString(),
        }),
        ...(property?.bathrooms && {
          numberOfBathroomsTotal: property.bathrooms.toString(),
        }),
        ...(property?.listPrice &&
          !isBuyDirectEligible(property) && {
            listPrice: property.listPrice,
          }),
        ...(property?.buyDirectPrice &&
          isBuyDirectEligible(property) && {
            listPrice: property.buyDirectPrice,
          }),
        image: coverImage,
        lat: property?.latitude,
        lon: property?.longitude,
        ...(property?.canSelfTour && { hasSelfTour: property?.canSelfTour }),
        addressToken: property?.addressToken,
      }}
    />
  );
  return (
    <>
      <Head>
        <link
          rel="preload"
          href="/fonts/graphik-regular/Graphik-Regular.woff2"
          as="font"
          type="font/woff2"
          crossOrigin="anonymous"
        />
        <link
          rel="preload"
          href="/fonts/graphik-semibold/Graphik-Semibold.woff2"
          as="font"
          type="font/woff2"
          crossOrigin="anonymous"
        />
        <link rel="preconnect" href="https://images.opendoor.com" />
        <link rel="dns-prefetch" href="https://images.opendoor.com" />
        <link rel="preconnect" href="https://cdn-cosmos.opendoor.com" />
        <link rel="dns-prefetch" href="https://cdn-cosmos.opendoor.com" />
        <meta httpEquiv="X-UA-Compatible" content="IE=edge,chrome=1" />
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width,initial-scale=1" />
        <meta httpEquiv="Accept-CH" content="DPR, Width, Viewport-Width" />
        <link
          rel="apple-touch-icon"
          sizes="144x144"
          href="https://www.opendoor.com/apple-touch-icon-144.png"
        />
      </Head>
      {seoHead}
      <GlobalStyles fontWeights={['regular', 'semibold', 'bold', 'medium']} />
      <Col backgroundColor="$backgroundPrimary">
        <Header
          slug="homes"
          mainNavLinks={resolvedMainNavLinks}
          menuPanelNavLinks={resolvedMenuPanelNavLinks}
          megaNavData={megaNavData}
          ctaTitle={navHeader?.ctaTitle}
          ctaLink={navHeader?.ctaLink}
        />
        <Box
          tag="main"
          tabIndex={-1}
          $platform-web={{ overflowX: 'hidden' }}
          backgroundColor="$backgroundPrimary"
        >
          {children}
        </Box>
      </Col>
    </>
  );
};

export default MarketplaceLayout;
